import { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper
} from "@tanstack/react-table";
import { Edit03Icon, Delete01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Table } from "shared/app/Table";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { MhvillageExternalMapping } from "api/mhvillage";

const columnHelper = createColumnHelper<MhvillageExternalMapping>();

interface CommunityMappingTableProps {
  mappings: MhvillageExternalMapping[];
  isLoading: boolean;
  communityLabel: string;
  communitiesLabel: string;
  onEdit: (mapping: MhvillageExternalMapping) => void;
  onDelete: (mapping: MhvillageExternalMapping) => void;
}

export const CommunityMappingTable = ({
  mappings,
  isLoading,
  communityLabel,
  communitiesLabel,
  onEdit,
  onDelete
}: CommunityMappingTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor("external_id", {
        id: "custom_fixed_width_external_id",
        header: "External ID",
        size: 200,
        meta: { headerClassName: "!pl-0" },
        cell: (info) => (
          <div className="box-border animate-appear flex h-12 max-h-12 items-center w-full pl-0 xl:pr-4 pr-1">
            <span className="font-medium text-content-1000">
              {info.getValue()}
            </span>
          </div>
        )
      }),
      columnHelper.display({
        id: "custom_community",
        header: communityLabel,
        cell: ({ row }) => {
          const mapping = row.original;
          if (mapping.property_id && mapping.property_name)
            return (
              <div>
                <p className="text-base font-medium text-content-1000">
                  {mapping.property_name}
                </p>
                <Show if={mapping.property_address}>
                  <p className="text-sm text-content-400">
                    {mapping.property_address}
                  </p>
                </Show>
              </div>
            );

          return <p className="text-base text-content-400">-</p>;
        }
      }),
      columnHelper.display({
        id: "custom_fixed_width_actions",
        size: 120,
        meta: { headerClassName: "pr-0" },
        cell: ({ row }) => (
          <div className="box-border animate-appear flex h-12 max-h-12 items-center w-full xl:pl-4 pl-1 pr-0 justify-end">
            <div className="flex items-center gap-5">
              <IconButton
                size={IconButtonSize.SM}
                variant={IconButtonVariant.BASE}
                borderLess
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row.original);
                }}
                HugeIcon={Edit03Icon}
                aria-label="Edit"
              />
              <IconButton
                size={IconButtonSize.SM}
                variant={IconButtonVariant.BASE}
                borderLess
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row.original);
                }}
                HugeIcon={Delete01Icon}
                aria-label="Delete"
              />
            </div>
          </div>
        )
      })
    ],
    [communityLabel, onEdit, onDelete]
  );

  const table = useReactTable({
    data: mappings,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table
      table={table}
      isLoading={isLoading}
      borderLess
      emptyStateHideIcon
      emptyStateTitle="No mappings yet"
      emptyStateText={`Add a record or import a CSV to map External IDs to your ${communitiesLabel.toLowerCase()}`}
      tableClassName={!mappings.length ? "!h-[280px]" : undefined}
    />
  );
};
